import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ClientTestimonials = ({ projects }) => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [displayedStats, setDisplayedStats] = useState({ client: 0, projects: 0, happy: 0 });

  const testimonials = projects
    .filter(project => project.testimonial)
    .map(project => ({
      ...project.testimonial,
      projectTitle: project.title,
      client: project.client,
      category: project.category,
      avatar: `https://randomuser.me/api/portraits/${Math.random() > 0.5 ? 'men' : 'women'}/${Math.floor(Math.random() * 50) + 1}.jpg`
    }));

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  useEffect(() => {
    // animate stats counting
    const animateCount = (target, setter) => {
      let count = 0;
      const step = Math.ceil(target / 50);
      const interval = setInterval(() => {
        count += step;
        if (count >= target) {
          count = target;
          clearInterval(interval);
        }
        setter(count);
      }, 20);
    };
    animateCount(98, val => setDisplayedStats(prev => ({ ...prev, client: val })));
    animateCount(32, val => setDisplayedStats(prev => ({ ...prev, projects: val })));
    animateCount(19, val => setDisplayedStats(prev => ({ ...prev, happy: val })));
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  if (testimonials.length === 0) return null;

  const sentence = {
  hidden: { opacity: 0 },
  visible: (i = 1) => ({
    opacity: 1,
    transition: { staggerChildren: 0.03, delayChildren: 0.03 * i }
  })
};

const letter = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 300, damping: 20 } }
};



  return (
    <div className="bg-secondary-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header */}
        <motion.div 
  className="text-center mb-12"
  initial="hidden"
  whileInView="visible"     // Animation triggers only when in viewport
  viewport={{ once: true, amount: 0.5 }} // Trigger when 50% visible, run only once
>
  {/* H2 with letter animation */}
  <motion.h2
    className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4 flex justify-center flex-wrap"
    variants={sentence}
    custom={0}
  >
    {"What Our Clients Say".split("").map((char, index) => (
      <motion.span key={index} variants={letter}>{char}</motion.span>
    ))}
  </motion.h2>

  {/* Paragraph with fade-up animation */}
  <motion.p
    className="text-xl text-text-secondary max-w-2xl mx-auto"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.8, duration: 0.8 }}
    viewport={{ once: true, amount: 0.5 }}
  >
    Don't just take our word for it. Here's what our clients have to say about working with us.
  </motion.p>
</motion.div>

        {/* Testimonial Card */}
        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0, x: 50, rotate: 5, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, rotate: 0, scale: 1 }}
              exit={{ opacity: 0, x: -50, rotate: -5, scale: 0.95 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              className="bg-surface rounded-2xl shadow-xl border border-border p-8 md:p-12"
            >
              <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
                
                {/* Avatar */}
                <motion.div
                  className="flex-shrink-0"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 220, damping: 14 }}
                >
                  <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-primary">
                    <Image
                      src={testimonials[currentTestimonial].avatar}
                      alt={testimonials[currentTestimonial].author}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </motion.div>

                {/* Content */}
                <div className="flex-1 text-center md:text-left">
                  {/* Quote */}
                  <div className="mb-6">
                    <Icon name="Quote" size={36} className="text-primary mb-4 mx-auto md:mx-0" />
                    <motion.blockquote
                      className="text-xl md:text-2xl text-text-primary font-medium leading-relaxed"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2, duration: 0.8 }}
                    >
                      "{testimonials[currentTestimonial].text}"
                    </motion.blockquote>
                  </div>

                  {/* Author Info */}
                  <motion.div
                    className="mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                  >
                    <cite className="text-lg font-semibold text-text-primary not-italic">
                      {testimonials[currentTestimonial].author}
                    </cite>
                    <p className="text-text-secondary">
                      {testimonials[currentTestimonial].position}
                    </p>
                  </motion.div>

                  {/* Project Info */}
                  <motion.div
                    className="flex flex-col sm:flex-row items-center justify-center md:justify-start space-y-2 sm:space-y-0 sm:space-x-6 text-sm text-text-secondary"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="Briefcase" size={16} />
                      <span>{testimonials[currentTestimonial].projectTitle}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Tag" size={16} />
                      <span>{testimonials[currentTestimonial].category}</span>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          {testimonials.length > 1 && (
            <>
              <motion.button
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.95 }}
                onClick={prevTestimonial}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-4 bg-surface border border-border rounded-full p-3 shadow-sm text-text-secondary hover:text-primary"
              >
                <Icon name="ChevronLeft" size={20} />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.95 }}
                onClick={nextTestimonial}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-4 bg-surface border border-border rounded-full p-3 shadow-sm text-text-secondary hover:text-primary"
              >
                <Icon name="ChevronRight" size={20} />
              </motion.button>
            </>
          )}
        </div>

        {/* Indicators */}
        {testimonials.length > 1 && (
          <div className="flex justify-center space-x-2 mt-6">
            {testimonials.map((_, index) => (
              <motion.button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                layout
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className={`w-3 h-3 rounded-full ${
                  index === currentTestimonial
                    ? 'bg-primary scale-125'
                    : 'bg-secondary-300 hover:bg-secondary-400'
                }`}
              />
            ))}
          </div>
        )}

        {/* Stats */}
        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 pt-6 border-t border-border">
          <div className="text-center">
            <motion.div
              className="text-4xl font-bold text-primary mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              {displayedStats.client}%
            </motion.div>
            <div className="text-text-secondary">Client Satisfaction</div>
          </div>
          <div className="text-center">
            <motion.div
              className="text-4xl font-bold text-primary mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {displayedStats.projects}+
            </motion.div>
            <div className="text-text-secondary">Projects Completed</div>
          </div>
          <div className="text-center">
            <motion.div
              className="text-4xl font-bold text-primary mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              {displayedStats.happy}+
            </motion.div>
            <div className="text-text-secondary">Happy Clients</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ClientTestimonials;